/**
 * Shared types for the reference-field mention editor.
 *
 * Authoring uses a two-step cascade, but the persisted output is flat:
 *
 *  - Typing "@" picks a prompt and inserts a transient `mentionedPromptAnswer`
 *    node purely to drive the field picker.
 *  - An auto-inserted "." then offers that prompt's fields (Answer / Document /
 *    Quote, branching on its data type). Picking one collapses the whole
 *    "@prompt." run into a single `referenceFieldMention` chip whose `id`
 *    encodes both the field and the prompt as `{field}::{promptObjectId}`.
 *
 * The flat `{field}::{promptObjectId}` shape is what the server-side reference
 * renderer resolves: it keys fields off `id` because only `id`/`label` survive
 * the backends' JSON naming policy unchanged. A bare field id (no "::") is a
 * legacy single-prompt mention that resolves against the reference's own prompt.
 */
import { PromptDataType } from "../../types/prompt-model";

/** A single selectable option in a suggestion dropdown (prompt or property). */
export interface ReferenceMentionItem {
  /** Set on inserted nodes; null for freshly-listed suggestions. */
  uuid?: string | null;
  /** Prompt objectIdentifier for a prompt; field key (e.g. "answer") for a property. */
  id: string;
  label: string;
  /** "filter-answer" for a prompt mention, "property" for a field property. */
  type: string;
  promptDataType?: PromptDataType | null;
  /** uuid of the owning prompt node — set on property items only. */
  parentMentionId?: string | null;
}

/** The attributes carried by an inserted `mentionedPromptAnswer` node. */
export interface PromptMentionAttrs {
  uuid: string | null;
  id: string | null;
  label: string | null;
  type: string | null;
  promptDataType: PromptDataType | null;
  properties: unknown[];
}

/** The attributes carried by an inserted `mentionProperty` node. */
export interface PropertyMentionAttrs {
  uuid: string | null;
  id: string | null;
  label: string | null;
  type: string | null;
  parentMentionId: string | null;
  promptDataType: PromptDataType | null;
}

/** Minimal shape needed to offer a prompt as a mentionable "@" source. */
export interface PromptMentionSource {
  objectIdentifier: string;
  label: string;
  promptDataType: PromptDataType;
}

/** A legacy "@" field mention option (resolves against the reference's own prompt). */
export interface ReferenceFieldMention {
  id: string;
  label: string;
}

/**
 * The legacy "@" mention options. Predates the "@"/"." cascade: each resolves a
 * single field against the reference's own prompt (no prompt disambiguation).
 * Still supported so templates authored in the old format keep rendering and
 * round-trip unchanged through edit/save, and continue to export.
 */
export const REFERENCE_FIELD_MENTIONS: ReferenceFieldMention[] = [
  { id: "quote", label: "Quote" },
  { id: "document", label: "Document Name" },
  { id: "answer", label: "Answer" },
];

/** A plain field option before it is decorated with per-node attributes. */
export interface FieldOption {
  id: string;
  label: string;
}

/**
 * The "List As" step, shown after picking Answer on a list-of-values prompt.
 * Picking it drills, via an auto-inserted ".", into the list options.
 */
export const LIST_AS_OPTION: FieldOption = { id: "list-as", label: "List As" };

/**
 * The list options shown after "List As": how to render the answer's values.
 * Ids are the final server-side field ids (`ReferenceFieldTypes`), so the
 * collapse uses them directly. "Text" lists the values inline (comma-joined)
 * but still reads as `…answer.list-as.text`.
 */
export const ANSWER_LIST_FORMATS: FieldOption[] = [
  { id: "answerBulletList", label: "Bulleted list" },
  { id: "answerNumberedList", label: "Numbered list" },
  { id: "answerTextList", label: "Text" },
];

/**
 * The field options offered after a prompt (the "." after "@prompt"), branching
 * on the prompt's data type. Picking document/quote (or answer on a non-list
 * prompt) collapses the run into a flat field chip; picking answer on a
 * list-of-values prompt drills into the list options first. Field ids must
 * match the values the server-side renderer resolves against
 * (`ReferenceFieldTypes`).
 */
export const getPromptTopProperties = (
  promptDataType: PromptDataType | null | undefined
): FieldOption[] => {
  switch (promptDataType) {
    case PromptDataType.Boolean:
      return [
        { id: "answer", label: "Answer" },
        { id: "document", label: "Document" },
        { id: "quote", label: "Quote" },
      ];
    case PromptDataType.ListOfValues:
      // Same top-level fields as any prompt; picking "Answer" then drills into
      // the list options (bulleted / numbered / text).
      return [
        { id: "answer", label: "Answer" },
        { id: "document", label: "Document" },
        { id: "quote", label: "Quote" },
      ];
    case PromptDataType.RangeOfValues:
      return [
        { id: "answer", label: "Answer" },
        { id: "document", label: "Document" },
        { id: "quote", label: "Quote" },
        { id: "minimum", label: "Minimum (coming soon)" },
        { id: "maximum", label: "Maximum (coming soon)" },
        { id: "sum", label: "Sum (coming soon)" },
      ];
    case PromptDataType.Text:
      return [
        { id: "answer", label: "Answer" },
        { id: "document", label: "Document" },
        { id: "quote", label: "Quote" },
      ];
    case PromptDataType.Generative:
      return [
        { id: "answer", label: "Answer" },
        { id: "document", label: "Document" },
      ];
    case PromptDataType.Date:
      return [
        { id: "answer", label: "Answer" },
        { id: "document", label: "Document" },
        { id: "quote", label: "Quote" },
      ];
    default:
      return [
        { id: "answer", label: "Answer" },
        { id: "document", label: "Document" },
      ];
  }
};

/** Separator packing the prompt object id into a field mention's `id`. */
export const REFERENCE_FIELD_ID_SEPARATOR = "::";

/** Builds the flattened field mention id: `{field}::{promptObjectId}`. */
export const buildReferenceFieldId = (field: string, promptObjectId: string): string =>
  `${field}${REFERENCE_FIELD_ID_SEPARATOR}${promptObjectId}`;

/**
 * The dot-notation path a field id represents, used for the chip's display
 * label (e.g. `answerNumberedList` → `answer.list-as.numbered`). Display only —
 * the node's `id` stays the flat `{field}::{promptId}` the renderer resolves.
 */
export const referenceFieldDotPath = (fieldId: string): string => {
  switch (fieldId) {
    case "answerBulletList":
      return "answer.list-as.bulleted";
    case "answerNumberedList":
      return "answer.list-as.numbered";
    case "answerTextList":
      return "answer.list-as.text";
    default:
      return fieldId;
  }
};

/**
 * Pulls the prompt object id out of a flattened field mention id, or returns
 * null for a bare (legacy single-prompt) id that carries no prompt.
 */
export const decodeReferenceFieldPromptId = (fieldId: string): string | null => {
  const separatorIndex = fieldId.indexOf(REFERENCE_FIELD_ID_SEPARATOR);
  if (separatorIndex < 0) return null;
  const promptId = fieldId.slice(separatorIndex + REFERENCE_FIELD_ID_SEPARATOR.length);
  return promptId.length > 0 ? promptId : null;
};

/**
 * Collects the distinct prompt object identifiers a template draws on. The
 * author's explicit prompt selection is the source of truth for which prompts a
 * reference links to; this stays available for callers that reconcile against
 * it. Reads both the flat `referenceFieldMention` chips (`{field}::{promptId}`)
 * that are persisted and the transient `mentionedPromptAnswer` node that exists
 * mid-authoring before a field is picked. Bare (legacy) field ids carry no
 * prompt and are ignored.
 */
export const collectPromptObjectIdsFromTemplate = (node: unknown): string[] => {
  const ids = new Set<string>();
  const walk = (n: any) => {
    if (n?.type === "mentionedPromptAnswer" && typeof n.attrs?.id === "string") {
      ids.add(n.attrs.id);
    }
    if (n?.type === "referenceFieldMention" && typeof n.attrs?.id === "string") {
      const promptId = decodeReferenceFieldPromptId(n.attrs.id);
      if (promptId) ids.add(promptId);
    }
    if (Array.isArray(n?.content)) n.content.forEach(walk);
  };
  walk(node);
  return [...ids];
};
